// Admin utilities for Trivia Plug
import { auth, db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// Admin user IDs - centralize here for consistency
export const ADMIN_USER_IDS = [
  'JkHFFaPmeKhix6P95TYF5MQfMLM2',  // Bonus admin account
  'lMu7gLKMSmNR4J5ykj8aLCRh5An1',  // From Firebase rules
  '25ASAtsXhQYFIR3wOX2m2TTaJxD3',  // From achievements
  'brA61W2sFmbFQxNt4e3WqaW2Dfw1',  // Current admin user
  // Add more admin user IDs here as needed
];

export const ADMIN_EMAILS = [
  'triviaplugpat@gmail.com'
];

// Check if current user is admin
export const isCurrentUserAdmin = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return false;

    // Check by user ID
    if (ADMIN_USER_IDS.includes(user.uid)) {
      return true;
    }

    // Check by email
    if (user.email && ADMIN_EMAILS.includes(user.email)) {
      return true;
    }

    // Check Firestore user document
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return userData.role === 'admin';
    }

    return false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

// Make a user an admin
export const makeUserAdmin = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { 
      role: 'admin',
      updatedAt: new Date()
    }, { merge: true });
    
    console.log('✅ User made admin successfully:', userId);
    return true;
  } catch (error) {
    console.error('❌ Error making user admin:', error);
    return false;
  }
};

// Get admin status with detailed debug info
export const getAdminDebugInfo = async () => {
  const user = auth.currentUser;
  if (!user) {
    return {
      isAdmin: false,
      reason: 'No authenticated user',
      debug: {
        currentUser: null,
        adminUserIds: ADMIN_USER_IDS,
        adminEmails: ADMIN_EMAILS
      }
    };
  }

  try {
    // Check by user ID
    const isAdminById = ADMIN_USER_IDS.includes(user.uid);
    
    // Check by email
    const isAdminByEmail = user.email && ADMIN_EMAILS.includes(user.email);
    
    // Check Firestore document
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : null;
    const isAdminByRole = userData?.role === 'admin';

    const isAdmin = isAdminById || isAdminByEmail || isAdminByRole;

    return {
      isAdmin,
      reason: isAdmin ? 'User is admin' : 'User is not admin',
      debug: {
        currentUser: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        },
        adminUserIds: ADMIN_USER_IDS,
        adminEmails: ADMIN_EMAILS,
        checks: {
          isAdminById,
          isAdminByEmail,
          isAdminByRole,
          userData: userData ? {
            role: userData.role,
            email: userData.email,
            fullName: userData.fullName
          } : null
        }
      }
    };
  } catch (error) {
    console.error('Error getting admin debug info:', error);
    return {
      isAdmin: false,
      reason: 'Error checking admin status',
      error: error.message,
      debug: {
        currentUser: {
          uid: user.uid,
          email: user.email
        },
        adminUserIds: ADMIN_USER_IDS,
        adminEmails: ADMIN_EMAILS
      }
    };
  }
};

// Verify and fix admin user document
export const verifyAdminUser = async () => {
  const user = auth.currentUser;
  if (!user) {
    console.log('❌ No authenticated user');
    return false;
  }

  try {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log('⚠️ User document missing, creating admin document...');
      await setDoc(userRef, {
        fullName: user.displayName || user.email.split('@')[0],
        email: user.email,
        role: 'admin',
        createdAt: new Date(),
        updatedAt: new Date()
      });
      console.log('✅ Created admin user document');
      return true;
    } else {
      const userData = userDoc.data();
      if (userData.role !== 'admin') {
        console.log('⚠️ User role is not admin, updating...');
        await setDoc(userRef, {
          role: 'admin',
          updatedAt: new Date()
        }, { merge: true });
        console.log('✅ Updated user role to admin');
        return true;
      }
      console.log('✅ User is already admin');
      return true;
    }
  } catch (error) {
    console.error('❌ Error verifying admin user:', error);
    return false;
  }
}; 